import React, { useCallback, useEffect, useMemo, useState, useImperativeHandle, forwardRef, useRef } from 'react'
import { Button, Row, Col, Popover, Typography, Input, Collapse, Checkbox, Tooltip , Badge, Alert, Spin} from 'antd'
import { useLocation, useHistory, withRouter } from 'react-router-dom';
import _ from 'lodash';
import classnames from 'classnames'
import { CaretDownOutlined } from '@ant-design/icons';
import { SearchIcon, CloseIcon, LoadingIcon, FilterIcon, FilterIconFilled } from "components/Icons";
import PerfectScrollbar from 'react-perfect-scrollbar'
import Vocabulary, { getVocabulary } from 'components/Vocabulary';
import { BASE_GUTTER, DRAWER_SIZE_DEFAULT, MODAL_SIZE_EXTRA_SMALL } from "constants/Layout";
import { useProductsAndSubProductsFilterContext } from 'contexts/ProductsAndSubProductsFilterContext'
import useUrlSearchParams from "hooks/useUrlSearchParams";
import ColumnFilterKeyValue from './ColumnFilterKeyValue';
import useUpdateEffect from 'hooks/useUpdateEffect';
import { isFallingUnderGroup } from 'utils/armorcodeGroupUtil';
import { parseURLSearch, updateURLSearch } from 'utils/urlUtil';
import ACMiddleEllipsis from "ac-components/ac-middle-ellipsis/ACMiddleEllipsis";
import { stringSorter } from 'utils/sorters';

const ProductsAndSubProductsFilter = forwardRef(({className, businessUnitId, disabled=false, updateUrl=false, afterChange=_.noop, fieldKey, preSelectedData={}, disableProductOnEmptySubProduct=false, preAccessOnAllProductAndSubProductsIds=[], tagFilter = false, ...props}, ref) => {
    const history = useHistory();
    const location = useLocation();
    const { state, setLoadingOnTreeNode } = useProductsAndSubProductsFilterContext();
    const selectedState = useMemo(() => state?.[businessUnitId] || {}, [state, businessUnitId])
    const listing = useMemo(() => {
        const listing = _.map(selectedState?.products || [], p => {
            const obj = { ...p };
            let tagsList = undefined;
            if(p.tags){
                tagsList = p.tags.split(",");
            }
            obj.tags = tagsList;
            return obj;
        })
        return listing;
    }, [selectedState]);
    const [selectedTags, setSelectedTags] = useState([]);
    const [visible, setVisible] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [selectedData, setSelectedData] = useState({});
    const { filters: { product, subProduct } } = useUrlSearchParams();
    const thisRef = useRef();

    const { defaultProducts, defaultSubProducts, constructDefaultsFromArray } = props;
    useEffect(() => {
        // If we have a plain list of products and subproducts, the we can use this flow to construct the hierarchy
        if(!constructDefaultsFromArray){
            return;
        }
        const data = {};
        if(!_.isEmpty(defaultProducts) && !_.isEmpty(defaultSubProducts)){
            _.forEach(defaultProducts, pId => {
                const obj = listing.find(list => list.id === +pId);
                const spIdList = _.filter(obj?.subProducts, sp => defaultSubProducts.includes(+sp.id)).map(sp => sp.id);
                data[pId] = spIdList;
            })
        }
        setSelectedData(prev => {
            if(_.isEmpty(data) && _.isEmpty(prev)){
                return prev;
            } else {
                afterChange({data, businessUnitId, fieldKey});
                return data;
            }
        });
    }, [listing, defaultProducts, defaultSubProducts, afterChange, businessUnitId, fieldKey, constructDefaultsFromArray]);

    useEffect(() => {
        if (!_.isEmpty(preSelectedData) && !_.isEmpty(listing) && !thisRef.current) {
            const data = {...preSelectedData };
            Object.keys(data).forEach(productId => {
                if (!_.isEmpty(preAccessOnAllProductAndSubProductsIds) && preAccessOnAllProductAndSubProductsIds.includes(+productId)) {
                    const obj = listing.find(list => list.id === +productId);
                    if (obj) {
                        data[productId] = obj.subProducts.map(sub => sub.id);
                    }
                }
            })
            thisRef.current = true;
            setSelectedData(data);
        }
    }, [preSelectedData, listing, preAccessOnAllProductAndSubProductsIds])

    const setSelectedDataFromParams = useCallback(() => {
        const data = {};
        product.forEach(pId => {
            const obj = listing.find(l => l.id === pId)
            if (obj) {
                const subProducts = _.isUndefined(subProduct) ? obj.subProducts : obj.subProducts.filter(sub => _.includes(subProduct, sub.id))
                data[pId] = subProducts.map(sub => sub.id);
            }
        })
        if (!_.isEmpty(data)) {
            setSelectedData(data)
        }
    }, [product, subProduct, listing])

    const checkProductsAndSubProductsInParams = useMemo(() => {
        const keys = Object.keys(selectedData);
        const update = (product || []).every(p => {
            return keys.findIndex(s => +s.id === p) !== -1
        })
        return update;
    }, [product, selectedData])

    useEffect(() => {
        if (updateUrl && _.isEmpty(selectedData) && !_.isEmpty(listing) && !_.isEmpty(product) && !checkProductsAndSubProductsInParams) { // set product and subProducts from params
            setSelectedDataFromParams()
        }
    }, [selectedData, updateUrl, setSelectedDataFromParams, listing, product, checkProductsAndSubProductsInParams])

    const setTreeData = useCallback(() => {
        if (businessUnitId && !state?.hasOwnProperty(businessUnitId)) {
            setLoadingOnTreeNode(businessUnitId);
        }
    }, [businessUnitId, state, setLoadingOnTreeNode])

    useEffect(setTreeData, [setTreeData])

    const onSearch = useCallback(e => setSearchText(e.target.value || ""), []);
    
    const filteredData = useMemo(() => {
        const text = (searchText || '').trim().toLowerCase();
        let filteredList = listing;
        if(text){
            filteredList = filteredList.filter(item => {
                return item.name.toLowerCase().includes(text);
            })
        }
        if(!_.isEmpty(selectedTags)){
            filteredList = filteredList.filter(item => {
                if(item.tags){
                    return selectedTags.some(tag => {
                        return filterKeyValuePairs(item.tags, tag);
                    })
                }
                return false;
            })
        }
        return filteredList;
    }, [searchText, listing, selectedTags])

    const triggerAfterChange = useCallback((data={}) => {
        if (updateUrl) {
            const { filters, pagination } = parseURLSearch(location.search);
            const productIds = Object.keys(data);
            const subProductIds = productIds.reduce((acc, curr) => {
                if (!_.isEmpty(data[curr])) {
                    acc.push(...data[curr])
                }
                return acc;
            }, [])
            pagination.page = 0;

            // Do not pass produt filter if there are subproduct filters present. Filters work with AND operation,
            // therefore if multiple product and subproducts are selected conditions will never match. So only pass subproducts.
            filters.product = (!_.isEmpty(productIds) && _.isEmpty(subProductIds)) ? productIds : undefined;
            filters.subProduct = !_.isEmpty(subProductIds) ? subProductIds : undefined;
            updateURLSearch(history, { filters, pagination });
            return;
        }
        afterChange({data, businessUnitId, fieldKey, name: props.name})
    }, [updateUrl, afterChange, businessUnitId, fieldKey, props.name, location.search, history])

    const handleButtonClick = useCallback((value) => {
        if (disabled || selectedState.loading) {
            setVisible(false);
            return;
        }
        setVisible(value)
        if (!value) {
            triggerAfterChange(selectedData)
        }
    }, [disabled, selectedState, selectedData, triggerAfterChange])

    const resetSelectedData = useCallback(() => {
        triggerAfterChange()
        setSelectedData({})
    }, [triggerAfterChange])

    const getAllListingData = useCallback(() => {
        return listing;  
    }, [listing])

    useImperativeHandle(ref,() => ({
        resetSelectedData,
        getAllListingData,
    }), [resetSelectedData, getAllListingData]);

    const popoverContent = (
        <div style={{ width: DRAWER_SIZE_DEFAULT }}>
            <Row gutter={BASE_GUTTER}>
                <Col span={24}>
                    <Typography.Text>Select <Vocabulary name="product" />(s) & <Vocabulary name="subProduct" />(s)</Typography.Text>
                </Col>
                <Col span={12}>
                    <Input autoFocus className="data-list-search" suffix={<SearchIcon />} onChange={onSearch} allowClear placeholder={`Search ${getVocabulary("product")}(s)`} />
                </Col>
                <Col span={12}>
                    {tagFilter ? <TagFilter selectedTags={selectedTags} setSelectedTags={setSelectedTags} /> : null}
                </Col>
                <Col span={24}>
                    <div style={{ height: MODAL_SIZE_EXTRA_SMALL }}>
                        <PerfectScrollbar>
                            <ProductSubProductList visible={visible} products={filteredData} selectedData={selectedData} setSelectedData={setSelectedData} 
                                businessUnitId={businessUnitId} updateUrl={updateUrl} disableProductOnEmptySubProduct={disableProductOnEmptySubProduct}
                            />
                        </PerfectScrollbar>
                    </div>
                </Col>
            </Row>
        </div>
    )

    return (
        <Popover trigger={["click"]} visible={visible} content={popoverContent} onVisibleChange={handleButtonClick} placement="bottomLeft">
            <div>
                <Button className={classnames('w-100', className)} disabled={disabled}>
                    <div className="d-flex align-items-center justify-content-between">
                        <div className="text-left w-100">
                            <ProductsAndSubProductsLabel selectedData={selectedData} selectedState={selectedState} resetSelectedData={resetSelectedData}/>          
                        </div>
                        <div className='m-l-m'>
                            <CaretDownOutlined />
                        </div>
                    </div>
                </Button>
            </div>
        </Popover>
    )
})

function TagFilter({ setSelectedTags }) {
    const [visible, setVisible] = useState(false);
    const [tempSelectedTags, setTempSelectedTags] = useState([]);
    useUpdateEffect(() => setSelectedTags(tempSelectedTags), [tempSelectedTags]);
    const confirm = useCallback(() => {
        setVisible(false);
    }, []);

    const clear = useCallback(() => {
        setTempSelectedTags([]);
        setVisible(false);
    }, []);

    const FilterIconComponent = _.isEmpty(tempSelectedTags) ? FilterIcon : FilterIconFilled;

    return (
        <Popover
            visible={visible}
            onVisibleChange={setVisible}
            placement="bottomLeft"
            trigger="click"
            content={
                <div style={{ margin: "-12px -16px" }}>
                    <ColumnFilterKeyValue
                        setSelectedKeys={setTempSelectedTags}
                        clearFilters={clear}
                        confirm={confirm}
                        selectedKeys={tempSelectedTags}
                        visible={visible}
                    />
                </div>
            }
        >
            <div className='d-flex h-100 align-items-center clickable'>
                <FilterIconComponent className={classnames("m-r-xs", {"color-primary": !_.isEmpty(tempSelectedTags)})} />Filter <Vocabulary name={"product"} /> by tag
            </div>
        </Popover>
    );
}

export default withRouter(ProductsAndSubProductsFilter); // default export with router

export {
    ProductsAndSubProductsFilter // export without router
}

const entityLabelStyle = {
    maxWidth: "38%",
    textOverflow: "ellipsis",
    overflow: "hidden",
    float: "left"
}
const entityLabelStyleWithCountBadge = {
    ...entityLabelStyle,
    maxWidth: "30%",
}
const ProductsAndSubProductsLabel = ({selectedData, selectedState, resetSelectedData}) => {
    const subProductsCount = useMemo(() => {
        return Object.keys(selectedData).reduce((acc, curr) => {
            if (selectedData.hasOwnProperty(curr)) {
                acc += (selectedData[curr] || '').length
            }
            return acc;
        }, 0);
    }, [selectedData]);

    const handleReset = useCallback(event => {
        event.stopPropagation();
        resetSelectedData({})
    }, [resetSelectedData])

    if (_.isEmpty(selectedData)) {
        return <div className='w-100'>
            <div className='pull-left'>Select&nbsp;</div>
            <div style={entityLabelStyle}><Vocabulary name="product" />/s</div>
            <div className='pull-left'>&nbsp;&&nbsp;</div>
            <div style={entityLabelStyle}><Vocabulary name="subProduct" />/s</div> 
            {selectedState?.loading && <Spin className='pull-right' indicator={<LoadingIcon  style={{ fontSize: 14 }} spin/>} />}
        </div>;
    }
    return (
        <>
            <div style={entityLabelStyleWithCountBadge}><Vocabulary name="product" isPlural={true} />&nbsp;</div>
            <div className='pull-left'><BadgeCount className="m-r-xs" count={Object.keys(selectedData).length} /></div>
            <div style={entityLabelStyleWithCountBadge}><Vocabulary name="subProduct" isPlural={true} />&nbsp;</div>
            <div className='pull-left'><BadgeCount count={subProductsCount} /></div> 
            <Tooltip title="Clear"><CloseIcon className='pull-right' style={{ fontSize: 14, marginTop: 2}} onClick={handleReset} /></Tooltip>
        </>
    )
}

const BadgeCount = ({count, className}) => {
    return <Badge className={classnames("no-border", className)} count={count} style={{backgroundColor: '#35198e', marginRight: 4}} />
}

const ProductSubProductList = ({ visible, products, updateUrl, disableProductOnEmptySubProduct, ...restProps }) => {
    if (_.isEmpty(products)) {
        return <Alert type="info" description={<><Typography.Text strong>Note: </Typography.Text> <Typography.Text>No <Vocabulary name="product" isPlural={true} /> and <Vocabulary name="subProduct" isPlural={true} /> has been found.</Typography.Text></>} />;
    }
    return (
        <Collapse defaultActiveKey={['1']} className="pro-subPro-filter" bordered={false}  expandIconPosition="left"
            expandIcon={({ isActive }) => <Tooltip placement="topLeft" title={`Click to ${isActive ? "Collapse" : "Expand"}`}><CaretDownOutlined rotate={isActive ? 0 : -90} style={{ fontSize: 16, paddingTop:2 }} /></Tooltip>}
        >
            { products.map(product => <ProductDetail key={product.id} productData={product} {...restProps} updateUrl={updateUrl} disableProductOnEmptySubProduct={disableProductOnEmptySubProduct}/>) }
        </Collapse>
    )
}

const ProductDetail = ({productData, selectedData, setSelectedData, businessUnitId, updateUrl, disableProductOnEmptySubProduct, ...restProps}) => {
    const productIds = useMemo(() => Object.keys(selectedData), [selectedData])

    const preventPanelActionOnCheckBoxClick = useCallback((e) => e.stopPropagation(), []);

    const productChange = useCallback((event, product, _partiallySelected) => {
        event.stopPropagation();
        const data = {...selectedData};

        if (event.target.checked) {
            data[product.id] = _.map(product.subProducts || [], entry => +entry.id);
        } else {
            delete data[product.id];
        }
        setSelectedData(data)
    }, [selectedData, setSelectedData])

    const subProductChange = useCallback((event, fieldData) => {
        const {id, parent} = fieldData;
        event.stopPropagation();
        const data = {...selectedData};
        if (data.hasOwnProperty(parent)) {
            let subProducts = data[parent];
            if (event.target.checked) {
                subProducts.push(id)
            } else {
                subProducts = subProducts.filter(sub => sub !== id)
            }
            data[parent] = subProducts;
        } else {
            data[parent] = [id];
        }
        if (data[parent].length === 0) {
            delete data[parent];
        }
        setSelectedData(data)
    }, [selectedData, setSelectedData])


    const hasSelectedProduct = useMemo(() => {
        return productIds.includes(''+productData.id)
    }, [productIds, productData.id])

    const checkIfAllSubProductsAreSelected = useCallback((product) => {
        const teamSubProductsLength = product.subProducts?.length || 0;
        if (teamSubProductsLength === 0 && disableProductOnEmptySubProduct) {
            return 'disabled';
        }
        const teamSubProductsSelected = _.filter(product.subProducts, entry => (selectedData[+product.id] || []).indexOf(entry.id) !== -1);

        return (teamSubProductsLength === teamSubProductsSelected.length && hasSelectedProduct) ? 'full' : (teamSubProductsSelected.length > 0 ? 'partial' : 'none');
    }, [selectedData, hasSelectedProduct, disableProductOnEmptySubProduct]);

    const sortedSubProducts = useMemo(() => {
        if (_.isEmpty(productData.subProducts)) {
            return productData.subProducts;
        }
        return productData.subProducts.sort(stringSorter('name'));
    }, [productData.subProducts]);

    const productStatus = checkIfAllSubProductsAreSelected(productData);
    const partiallySelected = productStatus === 'partial';
    const productDisabled = productStatus === 'disabled';
    const checkedStatus = updateUrl ? (productStatus === 'full' && !productDisabled) : hasSelectedProduct;
    
    return (
    <Collapse.Panel {...restProps}
        header={<div className='d-flex' style={{ gap: 8 }}>
            <Checkbox disabled={productDisabled} checked={checkedStatus} indeterminate={partiallySelected && !productDisabled} onClick={preventPanelActionOnCheckBoxClick} onChange={event => productChange(event, productData, partiallySelected)} />
            <ACMiddleEllipsis text={productData.name} applyWidth />
            {!_.isEmpty(selectedData[productData.id]) && <Typography.Text style={{color: "#35198e"}}>{`(${selectedData[productData.id].length}) selected`}</Typography.Text>}
            </div>
        }
    >
            <Row className='m-0' justify="start" align='middle' gutter={BASE_GUTTER}>
                {_.isEmpty(sortedSubProducts) && <Col span={24}>No <Vocabulary name="subProduct" isPlural={true} /></Col>}
                {
                    sortedSubProducts.map(subProduct => {
                        let hasChecked = hasSelectedProduct;
                        if (hasChecked) {
                            hasChecked = selectedData[productData.id].some(s => s === subProduct.id)
                        }
                        return (
                            <Col span={24} key={subProduct.id}>
                                <div className='d-flex' style={{ gap: 8 }}>
                                    <Checkbox checked={hasChecked} onChange={event => subProductChange(event, {...subProduct, parent: productData.id})} />
                                    <ACMiddleEllipsis text={subProduct.name} className='flex-1' />
                                </div>
                            </Col>
                        )
                    })
                }
            </Row>
        </Collapse.Panel>
    ) 
}

export const filterKeyValuePairs = (tags, keyObj) => {
    if (_.isString(tags)) {
        tags = _.split(tags, ',');
    }
    return _.some(tags, tagString => {
        const [key, ...valueParts] = tagString.split(":");
        const value = valueParts.join(':');
        let match = false;
        if (keyObj.key) {
            match = key.toLowerCase().includes(keyObj.key.toLowerCase());
        }
        if (match && keyObj.value) {
            let matchValue;
            // example - a/b/.*
            // This will check for the exact hierarchy which is a/b
            if (_.endsWith(keyObj.value, '/.*')) {
                matchValue = isFallingUnderGroup(keyObj.value, value);
            }
            // This will match its children hierarchies which is a/b/.*
            if (!matchValue) {
                matchValue = value.toLowerCase().includes(keyObj.value.toLowerCase());
            }
            match = matchValue === true;
        }
        return match;
    });
}
